import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/Atoms/Button/Button';
import { useDialog } from '@/components/Molecules/Dialog/Dialog';
import { LimitReachedDialog } from '@/components/Molecules/LimitReachedDialog/LimitReachedDialog';
import { OfflineDownloadButton } from '@/components/Molecules/OfflineDownloadButton/OfflineDownloadButton';
import { SaveButton } from '@/components/Molecules/SaveButton/SaveButton';
import { SubscribeButton } from '@/components/Molecules/SubscribeButton/SubscribeButton';
import { ShareLinkButton } from '@/components/Organisms/ShareLinkButton/ShareLinkButton';
import useTranslation from '@/hooks/useTranslation';
import type { ContentType, IAttachment, IInsightNoteDetail } from '@/interfaces';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import DownloadIcon from '@bloomberg/web-icons-react/Download';
import FileIcon from '@bloomberg/web-icons-react/File';
import FileExcelIcon from '@bloomberg/web-icons-react/FileExcel';
import FilePowerpointIcon from '@bloomberg/web-icons-react/FilePowerpoint';
import type { ShareAction } from 'bnef';
import type { Translate } from 'next-translate';
import { useRouter } from 'next/router';
import type { ComponentProps, MouseEvent, ReactNode } from 'react';
export type KeyedAttachments = {
  [index: string]: AttachmentWithAllowance;
};
export type AttachmentWithAllowance = IAttachment & {
  withinLimit: boolean;
};
export const useInsightExecSumActions: (props: InsightExecSumActionsProps) => ReactNode[] = t0 => {
  const $ = _c(29);
  const {
    insight,
    shareToken,
    isModel,
    isDataset,
    attachmentsByType,
    watermark
  } = t0;
  const {
    t
  } = useTranslation("insights");
  const router = useRouter();
  const lang = router.query?.language as string;
  let result;
  if ($[0] !== attachmentsByType || $[1] !== insight || $[2] !== isDataset || $[3] !== isModel || $[4] !== lang || $[5] !== shareToken || $[6] !== t || $[7] !== watermark) {
    let actions;
    if (isDataset) {
      actions = getDatasetActions(insight, attachmentsByType, t("insights:InsightExecSumActions.viewCoversheet"), "Insight: View Coversheet", t("insights:InsightExecSumActions.downloadData"), "Insight: Download Data", t);
    } else {
      if (isModel) {
        actions = getDatasetActions(insight, attachmentsByType, t("insights:InsightExecSumActions.viewUserGuide"), "Insight: View User Guide", t("insights:InsightExecSumActions.downloadModel"), "Insight: Download Model", t);
      } else {
        actions = getInsightActions(insight, attachmentsByType, t, lang);
      }
    }
    const t1 = `Insight: ${insight.id}`;
    let t2;
    if ($[9] !== insight.id || $[10] !== insight.title || $[11] !== insight.type || $[12] !== t1) {
      t2 = <SaveButton key="save" id={insight.id} type={insight.type} itemName={insight.title} analyticsBaseId={t1} />;
      $[9] = insight.id;
      $[10] = insight.title;
      $[11] = insight.type;
      $[12] = t1;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    result = [t2];
    if (insight.parentSeries && !insight.parentSeries.isArchived) {
      let t3;
      if ($[14] !== insight.parentSeries) {
        t3 = <SubscribeButton key="subscribe" series={insight.parentSeries} />;
        $[14] = insight.parentSeries;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      result.push(t3);
    }
    if (shareToken && attachmentsByType.pdf) {
      let t3;
      if ($[16] !== shareToken.url) {
        t3 = async () => shareToken.url;
        $[16] = shareToken.url;
        $[17] = t3;
      } else {
        t3 = $[17];
      }
      let t4;
      if ($[18] !== shareToken.remainingShares || $[19] !== t3) {
        t4 = <ShareLinkButton key="share" linkFetcher={t3} shareTokenRequired={true} shares={shareToken.remainingShares} />;
        $[18] = shareToken.remainingShares;
        $[19] = t3;
        $[20] = t4;
      } else {
        t4 = $[20];
      }
      result.push(t4);
    }
    if (attachmentsByType.pdf) {
      const t3 = insight.type as ContentType;
      const t4 = `${insight.url}/view`;
      const t5 = `/downloads/insights/${insight.id}/pdf`;
      let t6;
      if ($[21] !== attachmentsByType.pdf.withinLimit || $[22] !== insight.id || $[23] !== insight.title || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== watermark) {
        t6 = <OfflineDownloadButton key="offline" contentType={t3} itemId={insight.id} title={insight.title} viewerUrl={t4} pdfUrl={t5} watermarkText={watermark} canDownload={attachmentsByType.pdf.withinLimit} />;
        $[21] = attachmentsByType.pdf.withinLimit;
        $[22] = insight.id;
        $[23] = insight.title;
        $[24] = t3;
        $[25] = t4;
        $[26] = t5;
        $[27] = watermark;
        $[28] = t6;
      } else {
        t6 = $[28];
      }
      result.push(t6);
    }
    actions.forEach(action => {
      result.push(<ActionWithDialog key={action.url} action={action} />);
    });
    $[0] = attachmentsByType;
    $[1] = insight;
    $[2] = isDataset;
    $[3] = isModel;
    $[4] = lang;
    $[5] = shareToken;
    $[6] = t;
    $[7] = watermark;
    $[8] = result;
  } else {
    result = $[8];
  }
  return result;
};
export const ActionWithDialog = t0 => {
  const $ = _c(19);
  const {
    action,
    kind
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      lazy: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const dialog = useDialog(t1);
  let onClick;
  if (!action.canDownload) {
    let t2;
    if ($[1] !== dialog) {
      t2 = evt => {
        evt.preventDefault();
        dialog.open();
        return false;
      };
      $[1] = dialog;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    onClick = t2;
  }
  const t2 = action.icon;
  const t3 = kind ?? action.kind ?? "tertiary";
  const t4 = action.url;
  const t5 = onClick;
  const t6 = `Insight: ${action.trackingLabel}`;
  let t7;
  if ($[3] !== t6) {
    t7 = buildTrackingObject(TrackingAction.FollowLink, TrackingCategory.Attachment, t6);
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== action.icon || $[6] !== action.text || $[7] !== action.url || $[8] !== onClick || $[9] !== t3 || $[10] !== t7) {
    t8 = <Button icon={t2} kind={t3} href={t4} onClick={t5} data-analytics-tracking={t7}>{action.text}</Button>;
    $[5] = action.icon;
    $[6] = action.text;
    $[7] = action.url;
    $[8] = onClick;
    $[9] = t3;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== action.canDownload || $[13] !== action.limitMessageKey || $[14] !== dialog) {
    t9 = !action.canDownload && <LimitReachedDialog dialog={dialog} limitMessageKey={action.limitMessageKey} />;
    $[12] = action.canDownload;
    $[13] = action.limitMessageKey;
    $[14] = dialog;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
};
const getDatasetActions = (insight: IInsightNoteDetail, attachments: KeyedAttachments, coverText: string, coverTrackingLabel: string, downloadText: string, downloadTrackingLabel: string, t: Translate): IAction[] => {
  const actions: IAction[] = [];
  if (attachments.pdf) {
    actions.push({
      url: `${insight.url}/view`,
      icon: <FileIcon />,
      text: coverText,
      trackingLabel: coverTrackingLabel,
      canDownload: attachments.pdf.withinLimit,
      limitMessageKey: 'insight'
    });
  }
  if (attachments.zip) {
    actions.push({
      url: attachments.zip.url,
      icon: <DownloadIcon />,
      text: t('insights:InsightExecSumActions.downloadData'),
      trackingLabel: 'Download Data',
      kind: !attachments.excel ? 'primary' : undefined,
      canDownload: attachments.zip.withinLimit,
      limitMessageKey: 'data'
    });
  }
  if (attachments.excel) {
    actions.push({
      url: attachments.excel.url,
      icon: <FileExcelIcon />,
      text: downloadText,
      trackingLabel: downloadTrackingLabel,
      kind: 'primary',
      canDownload: attachments.excel.withinLimit,
      limitMessageKey: 'data'
    });
  }
  return actions;
};
const getInsightActions = (insight: IInsightNoteDetail, attachments: KeyedAttachments, t: Translate, lang?: string): IAction[] => {
  const actions: IAction[] = [];
  if (attachments.zip) {
    actions.push({
      url: attachments.zip.url,
      icon: <FileIcon />,
      text: t('common:ResourceType.data'),
      trackingLabel: 'Data',
      canDownload: attachments.zip.withinLimit,
      limitMessageKey: 'data'
    });
  }
  if (attachments.powerpoint) {
    actions.push({
      url: attachments.powerpoint.url,
      icon: <FilePowerpointIcon />,
      text: t('common:ResourceType.charts'),
      trackingLabel: 'Charts',
      canDownload: attachments.powerpoint.withinLimit,
      limitMessageKey: 'insight'
    });
  }
  if (attachments.excel) {
    actions.push({
      url: attachments.excel.url,
      icon: <FileExcelIcon />,
      text: t('common:ResourceType.data'),
      trackingLabel: 'Data',
      canDownload: attachments.excel.withinLimit,
      limitMessageKey: 'data'
    });
  }
  if (attachments.pdf) {
    actions.push(getReportAction(insight, attachments.pdf, t, lang));
  }
  return actions;
};
export const getReportAction = (insight: IInsightNoteDetail, pdfAttachment: AttachmentWithAllowance, t: Translate, lang?: string): IAction => {
  const langQuery = lang ? `?language=${lang}` : '';
  return {
    url: `${insight.url}/view${langQuery}`,
    icon: <FileIcon />,
    text: t('insights:InsightExecSumActions.viewFullReport'),
    trackingLabel: 'View Full Report',
    kind: 'primary',
    canDownload: pdfAttachment.withinLimit,
    limitMessageKey: 'insight'
  };
};
interface IAction {
  url: string;
  icon: ReactNode;
  text: string;
  trackingLabel: string;
  kind?: ComponentProps<typeof Button>['kind'];
  canDownload: boolean;
  limitMessageKey: 'insight' | 'data';
}
export interface InsightExecSumActionsProps {
  insight: IInsightNoteDetail;
  isModel: boolean;
  isDataset: boolean;
  shareToken: null | ShareAction;
  attachmentsByType: KeyedAttachments;
  watermark: string;
}